/* ============================================================
   NeonBlend Theme — JavaScript
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Scroll Reveal ---------- */
  function initReveal() {
    const els = document.querySelectorAll('.nb-reveal');
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach((el) => io.observe(el));
  }

  /* ---------- Mobile Nav Toggle ---------- */
  function initMobileNav() {
    const toggle = document.getElementById('nb-nav-toggle');
    const menu = document.getElementById('nb-nav-menu');
    const body = document.body;

    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      const isOpen = menu.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen);
      toggle.classList.toggle('is-active', isOpen);
      body.classList.toggle('nb-nav-open', isOpen);
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!toggle.contains(e.target) && !menu.contains(e.target)) {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('is-active');
        body.classList.remove('nb-nav-open');
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('is-active');
        body.classList.remove('nb-nav-open');
        toggle.focus();
      }
    });
  }

  /* ---------- Header Scroll State ---------- */
  function initHeaderScroll() {
    const header = document.getElementById('nb-header');
    if (!header) return;

    let lastY = 0;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const y = window.scrollY;
          if (y > 60) {
            header.classList.add('is-scrolled');
          } else {
            header.classList.remove('is-scrolled');
          }
          lastY = y;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------- Testimonials Carousel ---------- */
  function initTestimonials() {
    const carousel = document.querySelector('.nb-testimonials-carousel');
    if (!carousel) return;

    const track = carousel.querySelector('.nb-testimonials-track');
    const slides = carousel.querySelectorAll('.nb-testimonial-slide');
    const prevBtn = carousel.querySelector('.nb-carousel-prev');
    const nextBtn = carousel.querySelector('.nb-carousel-next');
    const dots = carousel.querySelectorAll('.nb-carousel-dot');

    if (!track || slides.length < 2) return;

    let current = 0;
    let autoPlay;

    function goTo(index) {
      slides[current].classList.remove('is-active');
      dots[current]?.classList.remove('is-active');
      current = (index + slides.length) % slides.length;
      slides[current].classList.add('is-active');
      dots[current]?.classList.add('is-active');
    }

    function startAuto() {
      autoPlay = setInterval(() => goTo(current + 1), 5000);
    }

    function stopAuto() {
      clearInterval(autoPlay);
    }

    slides[0].classList.add('is-active');
    dots[0]?.classList.add('is-active');

    prevBtn?.addEventListener('click', () => { stopAuto(); goTo(current - 1); startAuto(); });
    nextBtn?.addEventListener('click', () => { stopAuto(); goTo(current + 1); startAuto(); });
    dots?.forEach((dot, i) => dot.addEventListener('click', () => { stopAuto(); goTo(i); startAuto(); }));

    startAuto();

    // Pause on hover
    carousel.addEventListener('mouseenter', stopAuto);
    carousel.addEventListener('mouseleave', startAuto);

    // Touch/swipe support
    let touchStartX = 0;
    carousel.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    carousel.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        stopAuto();
        goTo(diff > 0 ? current + 1 : current - 1);
        startAuto();
      }
    });
  }

  /* ---------- Contact Form Handling ---------- */
  function initContactForm() {
    const form = document.getElementById('nb-audit-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = form.querySelector('[type="submit"]');
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Submitting…';

      // Shopify contact form uses action="/contact#contact_form"
      const data = new FormData(form);

      try {
        const res = await fetch('/contact', {
          method: 'POST',
          body: data,
          headers: { 'Accept': 'application/json' }
        });

        if (res.ok) {
          form.innerHTML = `
            <div class="nb-form-success">
              <div class="nb-icon-wrap" style="margin:0 auto var(--space-4)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h3 class="nb-heading-sm nb-text-center">Application Received!</h3>
              <p class="nb-text-muted nb-text-center" style="margin-top:var(--space-3)">
                We'll review your details and reach out within 1–2 business days to schedule your audit call.
              </p>
            </div>`;
        } else {
          throw new Error('Form submission failed');
        }
      } catch (err) {
        btn.disabled = false;
        btn.textContent = originalText;
        const errEl = form.querySelector('.nb-form-error') || document.createElement('p');
        errEl.className = 'nb-form-error';
        errEl.style.cssText = 'color:#DC2626;margin-top:var(--space-3);font-size:var(--text-sm);';
        errEl.textContent = 'Something went wrong. Please email us at hello@neonblend.ai';
        if (!form.contains(errEl)) form.appendChild(errEl);
      }
    });
  }

  /* ---------- Newsletter Form ---------- */
  function initNewsletterForm() {
    const form = document.querySelector('.nb-newsletter-form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = form.querySelector('input[type="email"]').value;
      if (!email) return;

      const btn = form.querySelector('button');
      btn.textContent = 'Subscribed ✓';
      btn.disabled = true;
      btn.style.background = '#059669';
      btn.style.borderColor = '#059669';
    });
  }

  /* ---------- Smooth Anchor Scrolling ---------- */
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach((a) => {
      a.addEventListener('click', (e) => {
        const target = document.querySelector(a.getAttribute('href'));
        if (target) {
          e.preventDefault();
          const offset = 80; // header height
          const top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    });
  }

  /* ---------- Case Studies Tab (Services page) ---------- */
  function initServiceTabs() {
    const tabs = document.querySelectorAll('.nb-service-tab');
    const panels = document.querySelectorAll('.nb-service-panel');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.classList.remove('is-active'));
        panels.forEach((p) => p.classList.remove('is-active'));
        tab.classList.add('is-active');
        const target = document.getElementById(tab.dataset.panel);
        if (target) target.classList.add('is-active');
      });
    });
  }

  /* ---------- Init ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initMobileNav();
    initHeaderScroll();
    initTestimonials();
    initContactForm();
    initNewsletterForm();
    initSmoothAnchors();
    initServiceTabs();
  });
})();
